<template>
  <div>
    <!-- GPU 数据面板 -->
    <div v-if="gpuData.length > 0" class="gpu-monitor">
      <div v-for="gpu in gpuData" :key="gpu.cudaIndex" class="gpu-card">
        <div class="gpu-header">
          <span class="gpu-name">显卡{{ Number(gpu.cudaIndex) + 1 }}</span>
          <span v-if="gpu.temperature" class="gpu-temp">{{ gpu.temperature }}°C</span>
        </div>
        <div class="gpu-row">
          <span class="gpu-label">核心</span>
          <span class="gpu-value">{{ gpu.cudaUtilization }}</span>
          <div class="gpu-bar-bg">
            <div class="gpu-bar-fill gpu-bar-gpu" :style="{ width: gpu.cudaUtilization }"></div>
          </div>
        </div>
        <div class="gpu-row">
          <span class="gpu-label">显存</span>
          <span class="gpu-value">{{ gpu.memoryPercent }}%</span>
          <div class="gpu-bar-bg">
            <div class="gpu-bar-fill gpu-bar-mem" :style="{ width: gpu.memoryPercent + '%' }"></div>
          </div>
        </div>
      </div>
      <!-- ComfyUI 任务队列 -->
      <div class="gpu-card queue-card">
        <div class="gpu-header">
          <span class="gpu-name">任务队列</span>
        </div>
        <div class="queue-row">
          <span class="queue-label">执行</span>
          <span class="queue-value" :class="{ 'queue-active': queueInfo && queueInfo.running > 0 }">{{ queueInfo ? queueInfo.running : '-' }}</span>
        </div>
        <div class="queue-row">
          <span class="queue-label">排队</span>
          <span class="queue-value" :class="{ 'queue-pending': queueInfo && queueInfo.pending > 0 }">{{ queueInfo ? queueInfo.pending : '-' }}</span>
        </div>
      </div>
    </div>
    <!-- 加载状态 -->
    <div v-else-if="gpuLoading" class="gpu-monitor gpu-status">
      <div class="gpu-card gpu-loading">
        <span class="gpu-loading-text">连接中...</span>
      </div>
    </div>
    <!-- 错误状态 -->
    <div v-else-if="gpuError" class="gpu-monitor gpu-status">
      <div class="gpu-card gpu-error" :title="gpuErrorMsg" @click="retryGpuMonitor">
        <span class="gpu-error-text">未连接</span>
      </div>
    </div>
  </div>
</template>

<script>
import Status from '@/utils/cudaOnLine'
import { getLastUsedBaseUrl } from '@/utils/comfyUiRequest'
import checkCuda from '@/utils/checkCuda'
import axios from 'axios'

// localStorage key（Edge 跟踪防护可能拦截，需 try-catch）
const STORAGE_KEY = 'comfyui_last_base_url'

export default {
  name: 'GpuMonitor',
  data () {
    return {
      gpuData: [],
      gpuTimer: null,
      gpuLoading: true,
      gpuError: false,
      gpuErrorMsg: '',
      consecutiveFailures: 0, // 连续失败计数
      queueInfo: null, // ComfyUI 任务队列信息 { running, pending }
      queuePollCount: 0, // 队列轮询计数器（控制低频请求）
      queueConsecutiveFails: 0 // 队列连续失败计数
    }
  },
  mounted () {
    this.startGpuMonitor()
  },
  beforeDestroy () {
    this.stopGpuMonitor()
  },
  methods: {
    // ---- localStorage 安全读写（兼容 Edge 跟踪防护） ----
    safeGetStorage (key) {
      try { return localStorage.getItem(key) } catch (e) { return null }
    },
    safeSetStorage (key, value) {
      try { localStorage.setItem(key, value) } catch (e) { /* Edge 跟踪防护拦截，静默忽略 */ }
    },

    // ---- GPU 监控主流程（未做任何改动） ----
    startGpuMonitor () {
      this.consecutiveFailures = 0
      this.fetchGpuData()
      this.gpuTimer = setInterval(() => {
        // 连续失败3次后停止轮询，避免服务器不可达时刷屏
        if (this.consecutiveFailures >= 3) {
          this.stopGpuMonitor()
          return
        }
        this.fetchGpuData()
      }, 3000)
    },
    stopGpuMonitor () {
      if (this.gpuTimer) {
        clearInterval(this.gpuTimer)
        this.gpuTimer = null
      }
    },
    // 重试监控（用户主动触发或服务器恢复后）
    retryGpuMonitor () {
      this.gpuLoading = true
      this.gpuError = false
      this.gpuData = []
      this.startGpuMonitor()
    },
    async fetchGpuData () {
      try {
        const data = await Status.get('/cudaOnline')
        this.gpuData = Object.values(data)
        this.gpuLoading = false
        this.gpuError = false
        this.consecutiveFailures = 0
        // 队列轮询：每 3 次 GPU 轮询才拉取 1 次队列（约 9 秒），大幅降低请求频率
        this.queuePollCount++
        if (this.queuePollCount % 3 === 1 || this.queueConsecutiveFails > 0) {
          this.fetchQueueInfo()
        }
      } catch (e) {
        console.error('[GPU监控] 请求失败:', e.message || e)
        this.gpuLoading = false
        this.gpuError = true
        this.gpuErrorMsg = e.message || '连接失败'
        this.consecutiveFailures++
      }
    },

    // ---- ComfyUI 任务队列（独立于 GPU 主流程） ----
    /**
     * 获取所有可用的 ComfyUI 服务器地址
     * 始终直连 ComfyUI 服务器（与 comfyUiRequest.js 同源，无 CORS 问题）
     * 合并所有来源并去重：当前会话 URL → localStorage 持久化 URL → cudaApi 已知列表
     */
    getComfyUIUrls () {
      const rawUrls = []

      // 1. 当前会话最后一次提交任务的 URL
      const lastUrl = getLastUsedBaseUrl()
      if (lastUrl) {
        this.safeSetStorage(STORAGE_KEY, lastUrl)
        rawUrls.push(lastUrl)
      }

      // 2. localStorage 持久化 URL（解决页面刷新后丢失）
      const storedUrl = this.safeGetStorage(STORAGE_KEY)
      if (storedUrl) rawUrls.push(storedUrl)

      // 3. 所有已知 ComfyUI 服务器
      const apiList = checkCuda.cudaApi || []
      apiList.forEach(item => {
        Object.values(item).forEach(url => {
          if (url) rawUrls.push(url)
        })
      })

      // 去重后返回，确保多张显卡的服务器都能被轮询到
      return [...new Set(rawUrls)]
    },
    /**
     * 拉取 ComfyUI 任务队列信息
     * - 低频轮询（约 9 秒一次），不会导致 ComfyUI 崩溃
     * - 连续 3 次失败后暂停队列请求，GPU 监控不受影响
     * - 请求失败静默处理，绝不弹窗、不改变 GPU 状态
     */
    async fetchQueueInfo () {
      // 连续 3 次失败后暂停队列轮询（ComfyUI 不可用时）
      if (this.queueConsecutiveFails >= 3) {
        return
      }
      const urls = this.getComfyUIUrls()
      if (urls.length === 0) {
        this.queueInfo = null
        return
      }
      // 并行请求所有 ComfyUI 服务器的 /queue 接口
      const results = await Promise.allSettled(
        urls.map(url => axios.get(url + '/queue', { timeout: 8000 }))
      )
      let totalRunning = 0
      let totalPending = 0
      let hasSuccess = false
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled') {
          const data = result.value.data
          totalRunning += Array.isArray(data.queue_running) ? data.queue_running.length : 0
          totalPending += Array.isArray(data.queue_pending) ? data.queue_pending.length : 0
          hasSuccess = true
        }
      })
      if (hasSuccess) {
        this.queueInfo = { running: totalRunning, pending: totalPending }
        this.queueConsecutiveFails = 0
      } else {
        this.queueConsecutiveFails++
        console.warn(`[任务队列] 请求失败 (${this.queueConsecutiveFails}/3)`, urls)
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import "@/style/common.less";

.gpu-monitor {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-left: 40px;

  &.gpu-status {
    .gpu-card {
      display: flex;
      align-items: center;
      justify-content: center;

      .gpu-loading-text {
        font-size: 11px;
        color: @font_gray;
        animation: blink 1.5s infinite;
      }

      .gpu-error-text {
        font-size: 11px;
        color: #ff6b6b;
        cursor: pointer;
      }
    }
  }

  .gpu-card {
    background: rgba(244, 244, 244, 0.85);
    border: 0px solid @basicselect;
    border-radius: @border_radius_3;
    padding: 5px 10px;
    color: @font_black;
    min-width: 135px;
    backdrop-filter: blur(6px);
    transition: box-shadow 0.3s;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .gpu-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 3px;

      .gpu-name {
        font-size: 11px;
        font-weight: 600;
      }

      .gpu-temp {
        font-size: 10px;
        color: @morejoy;
      }
    }

    .gpu-row {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 1px;

      .gpu-label {
        font-size: 10px;
        width: 26px;
        color: @font_gray;
      }

      .gpu-value {
        font-size: 10px;
        width: 36px;
        text-align: right;
        font-variant-numeric: tabular-nums;
        color: @font_black;
      }

      .gpu-bar-bg {
        flex: 1;
        height: 5px;
        background: @basicselect;
        border-radius: 3px;
        overflow: hidden;

        .gpu-bar-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.5s ease;
        }

        .gpu-bar-gpu {
          background: linear-gradient(90deg, @morejoy, #ff9a56);
        }

        .gpu-bar-mem {
          background: linear-gradient(90deg, #409eff, #67c8ff);
        }
      }
    }
  }

  // 任务队列卡片
  .queue-card {
    min-width: 80px;

    .queue-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1px;

      .queue-label {
        font-size: 10px;
        color: @font_gray;
      }

      .queue-value {
        font-size: 11px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        color: @font_black;
        transition: color 0.3s;
      }

      .queue-active {
        color: #ff9a56;
      }

      .queue-pending {
        color: #409eff;
      }
    }
  }
}

@keyframes blink {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
</style>
